// Catalogue helpers + the renderer that turns a `DateEvent` (user-created
// or sync'd public holiday — both come from /api/date-events) into
// vis-timeline `type: "background"` items, one per (port, day) pair.
//
// externalRef encoding (observed across the full /api/ports response):
// the first two characters are the ISO 3166-1 alpha-2 country code for
// every real port. Two prefixes are NOT real countries:
//
//   - "XV"  — WWL's pseudo-country for transit waypoints (CAPE RACE,
//             ARCTIC routes, PANAMA CANAL, GIBRALTAR-as-waypoint, …)
//   - "WA"  — single entry "AT SEA"
//
// Anything else with a 2-letter alpha prefix is assumed to be a real ISO
// country code that Nager.Date understands.

import type { DateEvent } from "../../../../backend/shared";
import type { TimelineItem } from "../../shared/vis-timeline/Timeline";

const NON_COUNTRY_PREFIXES = new Set(["XV", "WA"]);

// Day-after a YYYY-MM-DD as the same shape. Used to translate a missing
// endTime into "end of day = next-day 00:00". UTC arithmetic keeps the
// rollover stable across DST while we still emit naive-local strings —
// vis-timeline treats them as local.
function nextDayIso(yyyymmdd: string): string {
  const [y, m, d] = yyyymmdd.split("-").map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(next.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

// Effective time interval for a DateEvent, applying the day-boundary
// semantics for nullable startTime / endTime. Returns vis-timeline-shape
// naive-local ISO strings (no timezone suffix).
export function eventInterval(ev: DateEvent): { start: string; end: string } {
  const start = `${ev.startDate}T${ev.startTime ?? "00:00"}:00`;
  const end = ev.endTime
    ? `${ev.endDate}T${ev.endTime}:00`
    : `${nextDayIso(ev.endDate)}T00:00:00`;
  return { start, end };
}

export function countryFromRef(externalRef: string | undefined | null): string | null {
  if (!externalRef || externalRef.length < 2) return null;
  const prefix = externalRef.slice(0, 2).toUpperCase();
  if (!/^[A-Z]{2}$/.test(prefix)) return null;
  if (NON_COUNTRY_PREFIXES.has(prefix)) return null;
  return prefix;
}

// (port → country) lookup built from the catalogue. Ports without a
// derivable country are absent from the map (callers should treat misses
// as "no holidays applicable").
export function buildPortCountries(portRefs: Record<string, string>): Map<string, string> {
  const out = new Map<string, string>();
  for (const [port, ref] of Object.entries(portRefs)) {
    const c = countryFromRef(ref);
    if (c) out.set(port, c);
  }
  return out;
}

// Group selected ports by their derived country so the calendar can fetch
// /api/holidays once per country (instead of once per port).
export function selectedCountries(
  ports: readonly string[],
  portCountries: Map<string, string>,
): string[] {
  const seen = new Set<string>();
  for (const p of ports) {
    const c = portCountries.get(p);
    if (c) seen.add(c);
  }
  return [...seen].sort();
}

// vis-timeline background items: span the full day, attached to a specific
// group (port row) so the tint only paints the rows whose country observes
// the event. One item per (port, day-in-event-range).
export interface DateEventItem extends TimelineItem {
  type: "background";
}

const FALLBACK_KIND_COLOR = "#94a3b8"; // slate — for any kind not in eventKindColors

// Inline style vis-timeline reads for background items. 25%-alpha hex
// suffix so overlapping bands of different kinds blend visually.
function bgStyle(hex: string): string {
  return `background-color:${hex}40 !important;`;
}

// Build the set of terminals an event applies to:
//   - portName set       → just that terminal
//   - portGroup set      → every terminal in that group
//   - country only       → every terminal in any group whose country matches
export function eventTerminals(
  ev: DateEvent,
  ports: readonly string[],
  portCountries: Map<string, string>,
  portToGroup: Record<string, string>,
): string[] {
  if (ev.portName) {
    return ports.includes(ev.portName) ? [ev.portName] : [];
  }
  if (ev.portGroup) {
    return ports.filter((p) => portToGroup[p] === ev.portGroup);
  }
  if (ev.country) {
    return ports.filter((p) => portCountries.get(p) === ev.country);
  }
  return [];
}

// All date events flow through this — user-created and synced public
// holidays alike (both come from /api/date-events with a `source`
// discriminator). An event with country/port/terminal scope expands to
// one background item per matching terminal in the queried-port set.
//
// The item id is intentionally content-derived (scope + dates + kind)
// rather than ev.id. Every PUT /api/date-events re-issues IDs (the
// backend's replaceAll deletes and re-inserts every row), so a DB-id-
// based item id would force vis-timeline's DataSet to remove + re-add
// EVERY event item on every save. That mass churn raced with the no-op
// voyage-item updates and corrupted the stacking layout. Content-based
// ids only churn the items whose scope, dates, or kind actually changed.
export function dateEventsToTimelineItems(
  ports: readonly string[],
  events: readonly DateEvent[],
  portCountries: Map<string, string>,
  portToGroup: Record<string, string>,
  kindColors: Record<string, string>,
): DateEventItem[] {
  const out: DateEventItem[] = [];
  // Dedup against duplicate DB rows. The backend has no UNIQUE
  // constraint on (country, port_group, port_name, start_date, end_date,
  // kind, description), so a race in syncHolidaysForYear (or any other
  // double-insert) can produce two rows that hash to the same content
  // id below. vis-data throws "item with id ... already exists" when we
  // hand it both — a single bad row breaks the whole calendar render.
  // Cheap O(n) Set guard preserves the first occurrence and skips the rest.
  const seen = new Set<string>();
  for (const ev of events) {
    const targets = eventTerminals(ev, ports, portCountries, portToGroup);
    if (targets.length === 0) continue;
    const color = kindColors[ev.kind] ?? FALLBACK_KIND_COLOR;
    const scope = `${ev.country}|${ev.portGroup}|${ev.portName}`;
    const { start, end } = eventInterval(ev);
    // description + times are part of the id so two distinct rows with
    // the same scope/date/kind (rare but legal — backend has no unique
    // constraint) don't collapse into one DataSet entry, and a swap from
    // full-day to partial-day invalidates the prior DataSet entry.
    for (const port of targets) {
      const id = `event|${scope}|${ev.startDate}|${ev.endDate}|${ev.startTime ?? ""}|${ev.endTime ?? ""}|${ev.kind}|${ev.description}|${port}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({
        id,
        group: port,
        start,
        end,
        type: "background",
        content: "",
        style: bgStyle(color),
      });
    }
  }
  return out;
}
